import * as ApiConstant from './apiConstant';
import * as apiServiceBase from './apiServiceBase';
import { flattenObject } from './helpers';
import { IVehicleImage } from './interfaces/IVehicleImage';

interface IGetVehiclesBySearchRequest {
  filter: any;
  count?: string | number;
  orderBy?: any;
}

export type VehicleFieldGroupings =
  | '_4x4'
  | 'Convenience'
  | 'Dimensions_Weight'
  | 'Driver_Assistance'
  | 'Emergency_Assistance_Services'
  | 'Engine'
  | 'Environment'
  | 'Hybrid_Electric_Systems'
  | 'ID'
  | 'Infotainment_Navigation'
  | 'Interior_Comfort'
  | 'Performance'
  | 'Price'
  | 'Remote_Control'
  | 'Safety_Security'
  | 'Safety_Rating'
  | 'Servicing'
  | 'Towing'
  | 'Vehicle'
  | 'Warranty_Roadside_Assistance'
  | 'Wheels_Tyres'
  | 'ObjectiveScore'
  | 'tco';

// Fetch Vehicle By ID
export const ALL_VEHICLE_DATA_FIELDS: VehicleFieldGroupings[] = [
  '_4x4',
  'Convenience',
  'Dimensions_Weight',
  'Driver_Assistance',
  'Emergency_Assistance_Services',
  'Engine',
  'Environment',
  'Hybrid_Electric_Systems',
  'ID',
  'Infotainment_Navigation',
  'Interior_Comfort',
  'Performance',
  'Price',
  'Remote_Control',
  'Safety_Security',
  'Safety_Rating',
  'Servicing',
  'Towing',
  'Vehicle',
  'Warranty_Roadside_Assistance',
  'Wheels_Tyres',
  'ObjectiveScore',
  'tco',
];

/**
 * Get vehicle makes.
 *
 * @param paginationToken
 * @returns
 */
async function fetchMakes(paginationToken: any) {
  const response = await apiServiceBase.HttpGet(`${ApiConstant.getMakes.replace('{0}', paginationToken)}`);
  return response;
}

/**
 * Get vehicle models by make.
 *
 * @param make
 * @param paginationToken
 * @returns
 */
async function fetchModelsByMake(make: string, paginationToken: any) {
  const response = await apiServiceBase.HttpGet(`${ApiConstant.getModelsByMake.replace('{0}', make).replace('{1}', paginationToken)}`);
  return response;
}

/**
 * Get vehicle trims by make and model.
 *
 * @param make
 * @param model
 * @param paginationToken
 * @returns
 */
async function fetchTrimsByMakeAndModel(make: string, model: string, paginationToken: any = '') {
  const response = await apiServiceBase.HttpGet(
    `${ApiConstant.getTrimsByMakeAndModel.replace('{0}', make).replace('{1}', model).replace('{2}', paginationToken)}`
  );
  return response;
}

/**
 * Get vehicles by search.
 *
 * @param model
 * @returns
 */
async function fetchVehiclesBySearch(model: IGetVehiclesBySearchRequest) {
  return apiServiceBase.HttpPost(`${ApiConstant.getVehiclesBySearch}`, model);
}

/**
 * Get vehicles by search, results grouped by make/model/trim with a child array of variants
 *
 * @param model
 * @returns
 */
async function fetchVehiclesBySearchGrouped(model: IGetVehiclesBySearchRequest) {
  return apiServiceBase.HttpPost(`${ApiConstant.getVehiclesBySearchGrouped}`, model);
}

/**
 * Get a vehicle by ID, passing in any relevant fields you want to get back.
 * Fields is an array of fields that is split into a comma separated string.
 *
 * Note: AMS Vehicle data is flattened using the flattenObject helper.
 *
 * @param id
 * @param fieldGroupings
 *
 * @returns
 */
async function fetchVehicleById(id: string, fieldGroupings = ALL_VEHICLE_DATA_FIELDS): Promise<Record<string, any>> {
  if (!id) return {};
  const data = await apiServiceBase.HttpGet(`${ApiConstant.getVehicleById.replace('{0}', id).replace('{1}', fieldGroupings.join(','))}`);

  return flattenObject(data.vehicle[0], { exemptFields: ['tco'] });
}

/**
 * Get a list of images and their details for the vehicle
 *
 * @param id Unique identifier of the vehicle
 * @returns
 */
async function fetchVehicleImagesById(id: string): Promise<{ images: IVehicleImage[] }> {
  return apiServiceBase.HttpGet(`${ApiConstant.getVehicleImagesById.replace('{0}', id)}`);
}

/**
 * Get a list of reviews and their details for the vehicle
 *
 * @param id Unique identifier of the vehicle
 * @returns
 */
async function fetchVehicleReviewsById(id: string) {
  return apiServiceBase.HttpGet(`${ApiConstant.fetchVehicleReviewsById.replace('{0}', id)}`);
}

/**
 * Get reviews by search
 *
 * @param model
 * @returns
 */
async function fetchVehiclesReviewsBySearch(model: IGetVehiclesBySearchRequest) {
  return apiServiceBase.HttpPost(`${ApiConstant.fetchVehicleReviewsBySearch}`, model);
}

/**
 * Get review based on review id
 *
 * @param reviewId Unique identifier of the review
 * @returns
 */
async function fetchReviewsById(reviewId: string) {
  return apiServiceBase.HttpGet(`${ApiConstant.fetchReviewById.replace('{0}', reviewId)}`);
}

/**
 * Get vehicle variants based on trim_status_id
 *
 * @param trimId TrimStatusId of the vehicle e.g. toyota-camry-ascent-current
 * @param filterContext FilterContext from the trim-status/search result
 * @returns
 */
async function fetchVehicleGroupByTrimId(trimId: string, filterContext: string) {
  // Need to encode to handle because trimId can have a '+' character
  const encodedTrimId = encodeURIComponent(trimId);

  // Need to encode to handle because filterContext contains non-friendly url characters
  const encodedFilterContext = encodeURIComponent(filterContext);
  return apiServiceBase.HttpGet(`${ApiConstant.getVehicleGroupByTrimId.replace('{0}', encodedTrimId).replace('{1}', encodedFilterContext)}`);
}

export const carAdvisoryService = {
  fetchMakes,
  fetchModelsByMake,
  fetchTrimsByMakeAndModel,
  fetchVehiclesBySearch,
  fetchVehiclesBySearchGrouped,
  fetchVehicleById,
  fetchVehicleImagesById,
  fetchVehicleReviewsById,
  fetchVehiclesReviewsBySearch,
  fetchReviewsById,
  fetchVehicleGroupByTrimId,
};
